import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const statusLabels = {
  new: 'New',
  in_review: 'In Review',
  resolved: 'Resolved',
  spam: 'Spam',
};

const statusClasses = {
  new: 'bg-orange-100 text-[#FF6F00]',
  in_review: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  spam: 'bg-red-100 text-red-700',
};

const categoryLabels = {
  general: 'General',
  artist_support: 'Artist Support',
  collaborator_support: 'Collaborator Support',
  trust_safety: 'Trust & Safety',
};

const categoryHelp = {
  general: 'General platform question or account help.',
  artist_support: 'Artist profile, verification, media, or portfolio request.',
  collaborator_support: 'Collaborator profile, hiring, enquiry, or PMB request.',
  trust_safety: 'Safety, copyright, media removal, abuse, or urgent moderation.',
};

const AdminSupport = () => {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [busyId, setBusyId] = useState('');
  const [expandedId, setExpandedId] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState('');

  const focusId = searchParams.get('focus') || '';

  const loadMessages = async () => {
    const response = await adminApi.get('/admin/contact-messages');
    setMessages(response.data.messages || []);
    setCounts(response.data.counts || {});
    setLastLoadedAt(new Date().toLocaleTimeString());
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await adminApi.get('/admin/contact-messages');
        if (mounted) {
          setMessages(response.data.messages || []);
          setCounts(response.data.counts || {});
          setLastLoadedAt(new Date().toLocaleTimeString());
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    const intervalId = window.setInterval(load, 45000);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (focusId) {
      setExpandedId(focusId);
    }
  }, [focusId]);

  const categoryCounts = useMemo(() => {
    return messages.reduce(
      (acc, message) => {
        const category = message.category || 'general';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      },
      { general: 0, artist_support: 0, collaborator_support: 0, trust_safety: 0 }
    );
  }, [messages]);

  const openCount = (counts.new || 0) + (counts.in_review || 0);

  const filteredMessages = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return messages.filter((message) => {
      if (activeStatus !== 'all' && message.status !== activeStatus) return false;
      if (activeCategory !== 'all' && message.category !== activeCategory) return false;
      if (!normalizedSearch) return true;

      const haystack = [
        message.name,
        message.email,
        message.subject,
        message.message,
        message.page_url,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [activeCategory, activeStatus, messages, searchTerm]);

  const handleStatusChange = async (messageId, status) => {
    setBusyId(messageId);
    try {
      await adminApi.patch(`/admin/contact-messages/${messageId}/status`, { status });
      await loadMessages();
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this support message from the admin inbox?')) return;

    setBusyId(messageId);
    try {
      await adminApi.delete(`/admin/contact-messages/${messageId}`);
      await loadMessages();
    } finally {
      setBusyId('');
    }
  };

  const copyMessageSummary = async (message) => {
    const text = [
      `Subject: ${message.subject || 'Support message'}`,
      `From: ${message.name || 'Unknown'} <${message.email || ''}>`,
      `Category: ${categoryLabels[message.category] || 'General'}`,
      `Status: ${statusLabels[message.status] || 'New'}`,
      '',
      message.message || '',
      message.page_url ? `\nPage: ${message.page_url}` : '',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // Clipboard may be blocked in some browsers; the inbox text remains visible.
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Support Inbox</h2>
            <p className="text-gray-600 mt-3">
              Review Contact page messages, Trust & Safety reports, profile corrections, and support requests.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact?subject=Trust%20%26%20Safety" className="btn-outline text-center" target="_blank">
              Open Contact Page
            </Link>
            <button type="button" className="btn-primary" onClick={loadMessages}>
              Refresh Inbox
            </button>
          </div>
        </div>
        {lastLoadedAt && (
          <p className="mt-4 text-sm text-gray-500">Auto-refreshes every 45 seconds. Last checked: {lastLoadedAt}</p>
        )}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-[1.5rem] border border-purple-100 bg-gradient-to-br from-white to-purple-50 p-5 shadow-sm">
          <p className="text-sm text-gray-500">Needs Attention</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{openCount}</p>
        </div>
        {['all', 'new', 'in_review', 'resolved'].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setActiveStatus(status)}
            className={`rounded-[1.5rem] border p-5 text-left shadow-sm transition ${
              activeStatus === status
                ? 'border-[#FF6F00] bg-orange-50'
                : 'border-orange-100 bg-white hover:bg-orange-50'
            }`}
          >
            <p className="text-sm text-gray-500">{status === 'all' ? 'All Messages' : statusLabels[status]}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {status === 'all' ? messages.length : counts[status] || 0}
            </p>
          </button>
        ))}
      </section>

      <section className="rounded-[2rem] border border-orange-100 bg-white p-4 sm:p-5 shadow-lg">
        <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr_1fr]">
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
            placeholder="Search name, email, subject, message, or page URL"
          />
          <select
            value={activeCategory}
            onChange={(event) => setActiveCategory(event.target.value)}
            className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All categories</option>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label} ({categoryCounts[key] || 0})
              </option>
            ))}
          </select>
          <select
            value={activeStatus}
            onChange={(event) => setActiveStatus(event.target.value)}
            className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All statuses</option>
            {Object.entries(statusLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label} ({counts[key] || 0})
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveCategory(key)}
            className={`rounded-[1.5rem] border p-5 text-left transition ${
              activeCategory === key ? 'border-[#4A148C] bg-purple-50' : 'border-orange-100 bg-white hover:bg-orange-50'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-bold text-gray-900">{label}</h3>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
                {categoryCounts[key] || 0}
              </span>
            </div>
            <p className="mt-3 text-sm leading-6 text-gray-600">{categoryHelp[key]}</p>
          </button>
        ))}
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-500">Loading support messages...</div>
        ) : filteredMessages.length === 0 ? (
          <div className="p-6 text-gray-500">No support messages found.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {filteredMessages.map((message) => (
              <article
                key={message.id}
                className={`p-5 sm:p-6 space-y-4 transition ${
                  focusId === message.id ? 'bg-orange-50/70 ring-2 ring-inset ring-[#FF6F00]' : ''
                }`}
              >
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                  <div className="space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-xl font-semibold text-gray-900 break-words">{message.subject}</h3>
                      <span className={`${statusClasses[message.status] || statusClasses.new} px-3 py-1 rounded-full text-xs font-semibold`}>
                        {statusLabels[message.status] || 'New'}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold">
                        {categoryLabels[message.category] || 'General'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      From: <span className="font-semibold">{message.name}</span> -{' '}
                      <a className="allow-copy text-[#4A148C] underline" href={`mailto:${message.email}`}>
                        {message.email}
                      </a>
                    </p>
                    <p className="text-xs text-gray-500">
                      {message.created_at ? new Date(message.created_at).toLocaleString() : 'Date unavailable'}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="btn-outline"
                      disabled={busyId === message.id}
                      onClick={() => copyMessageSummary(message)}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => setExpandedId((current) => (current === message.id ? '' : message.id))}
                    >
                      {expandedId === message.id ? 'Hide' : 'Read'}
                    </button>
                    <select
                      value={message.status || 'new'}
                      onChange={(event) => handleStatusChange(message.id, event.target.value)}
                      disabled={busyId === message.id}
                      className="rounded-full border border-orange-200 px-4 py-2 text-sm font-semibold"
                    >
                      <option value="new">New</option>
                      <option value="in_review">In Review</option>
                      <option value="resolved">Resolved</option>
                      <option value="spam">Spam</option>
                    </select>
                    <button
                      type="button"
                      className="btn-outline border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(message.id)}
                      disabled={busyId === message.id}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {expandedId === message.id && (
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4 sm:p-5 space-y-4">
                    <p className="allow-copy whitespace-pre-wrap text-gray-800 leading-7">{message.message}</p>
                    {message.page_url && (
                      <p className="text-sm text-gray-500">
                        Page:{' '}
                        <a className="allow-copy text-[#4A148C] underline" href={message.page_url} target="_blank" rel="noreferrer">
                          {message.page_url}
                        </a>
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <a className="btn-primary" href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject || 'Kala Manch support')}`}>
                        Reply by Email
                      </a>
                      <button type="button" className="btn-outline" onClick={() => handleStatusChange(message.id, 'in_review')}>
                        Mark In Review
                      </button>
                      <button type="button" className="btn-outline" onClick={() => handleStatusChange(message.id, 'resolved')}>
                        Mark Resolved
                      </button>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSupport;
